// stores/appController.js
import { createBlockAtTop } from "./blockStore";
import { get } from "svelte/store";
import { format as formatDate } from "date-fns";
import logger from "../utils/logger";

// Import document store functionality
import {
  documents,
  currentDocument,
  documentJustLoaded,
  loadDocuments,
  loadDocument,
  createDocument,
  createTodayDocument,
  getTodayDocument,
  showFlash,
  blocks,
} from "./documentStore";

// Import router functionality
import {
  currentRoute,
  routeDocumentId,
  initRouter,
  navigateToDocument,
  navigateToHome,
  parseDocumentId,
  isDocumentRoute,
  isHomeRoute,
} from "./router";

// Track the last active date to detect day changes
let lastActiveDate = new Date().toDateString();

// Flag to track if application is already initialized
let appInitialized = false;

// Flag to prevent double document loading
let isLoadingDocument = false;

// Initialize the application
export async function initializeApp() {
  logger.info("Starting application initialization");

  // Prevent double initialization
  if (appInitialized) {
    logger.warn(
      "Application already initialized, skipping duplicate initialization",
    );
    return { alreadyInitialized: true };
  }

  try {
    // Load all documents first
    await loadDocuments();

    // Check current route first to determine initial document
    const currentHash = window.location.hash || "#/";
    let documentToLoad;

    if (isDocumentRoute(currentHash)) {
      // If we're on a document route, load that document
      const documentId = parseDocumentId(currentHash);
      documentToLoad = documentId;
    } else {
      // Otherwise, use the default document (today's)
      const todayDoc = getTodayDocument();
      if (todayDoc) {
        documentToLoad = todayDoc.id;
      } else {
        // Create today's document if it doesn't exist
        documentToLoad = await createTodayDocument();
        if (documentToLoad) {
          // Update URL silently without triggering another load
          window.location.hash = `#/document/${documentToLoad}`;
        }
      }
    }

    // Load the initial document if we have one
    let setupResult = { shouldFocus: false };
    if (documentToLoad) {
      await loadDocumentSafely(documentToLoad);

      // Now that we have blocks loaded, set up initial blocks
      setupResult = await setupInitialBlocks();
    }

    // Initialize the router with our route handler, but don't handle the initial route
    // since we've already loaded the document based on the URL
    initRouter((hash) => {
      // Only handle route changes after initialization
      if (appInitialized) {
        handleRouteChange(hash);
      }
    }, false); // false = don't handle initial route

    // Set up visibility change for day change detection
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Set up interval check for day changes
    setInterval(checkForNewDay, 60000); // Check every minute

    // Mark application as initialized
    appInitialized = true;

    logger.info("Application initialized successfully");
    return setupResult;
  } catch (error) {
    logger.error("Failed to initialize application", { error: error.message });
    showFlash("error", "Failed to initialize application");
    return false;
  }
}

// Handle route changes
export async function handleRouteChange(hash) {
  // Prevent handling routes during initialization or if already loading a document
  if (!appInitialized || isLoadingDocument) {
    logger.debug(
      "Skipping route handling - app not initialized or document already loading",
    );
    return;
  }

  logger.info("Handling route change", { hash });

  // Document route: #/document/:id
  if (isDocumentRoute(hash)) {
    const documentId = parseDocumentId(hash);
    const allDocs = get(documents);

    // Check if the document exists
    const docExists = allDocs.some((doc) => doc.id === documentId);

    // Don't reload the current document
    if (get(currentDocument)?.id === documentId) {
      logger.debug("Document already loaded, skipping", { documentId });
      return;
    }

    if (docExists) {
      logger.info("Switching to document from route", { documentId });
      await loadDocumentSafely(documentId);
    } else {
      logger.warn("Document not found in route, falling back to today", {
        documentId,
      });
      await loadDefaultDocument();
    }
    return;
  }

  // Home route or any unrecognized route
  await loadDefaultDocument();
}

// Safe wrapper for loadDocument to prevent duplicate loads
async function loadDocumentSafely(documentId) {
  if (isLoadingDocument) {
    logger.warn(
      "Document loading already in progress, skipping duplicate load",
    );
    return null;
  }

  try {
    isLoadingDocument = true;
    return await loadDocument(documentId);
  } finally {
    isLoadingDocument = false;
  }
}

// Load the default document (today's document)
export async function loadDefaultDocument() {
  logger.info("Loading default document");

  // Check if today's document exists
  const todayDoc = getTodayDocument();

  if (todayDoc) {
    logger.debug("Today's document exists, loading it", { id: todayDoc.id });
    const doc = await loadDocumentSafely(todayDoc.id);

    // Update the URL to match the document, but only if necessary
    if (typeof window !== "undefined" && doc) {
      if (window.location.hash !== `#/document/${todayDoc.id}`) {
        navigateToDocument(todayDoc.id, true); // true = silent navigation
      }
    }

    return doc;
  } else {
    logger.debug("Today's document does not exist, creating it");
    const newDocId = await createTodayDocument();

    if (newDocId) {
      const doc = await loadDocumentSafely(newDocId);
      if (typeof window !== "undefined") {
        navigateToDocument(newDocId, true); // true = silent navigation
      }
      return doc;
    }

    return null;
  }
}

// Handle visibility change
export async function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    logger.info("Tab became visible, checking for new day");
    await checkForNewDay();
  }
}

// Check for a new day and create document if needed
export async function checkForNewDay() {
  const currentDate = new Date().toDateString();

  // If the date has changed since we last checked
  if (currentDate !== lastActiveDate) {
    logger.info("Date changed, checking for today's document", {
      lastActiveDate,
      currentDate,
    });

    // Update the last active date
    lastActiveDate = currentDate;

    // Check if today's document exists
    const todayDoc = getTodayDocument();

    if (!todayDoc) {
      logger.info("Today's document not found, creating new one");
      // Create today's document and switch to it
      const newDocId = await createTodayDocument();
      if (newDocId) {
        await loadDocumentSafely(newDocId);
        navigateToDocument(newDocId);
      }
    } else if (get(currentDocument)?.id !== todayDoc.id) {
      // If today's document exists but we're not viewing it, switch to it
      logger.info("Switching to today's document");
      await loadDocumentSafely(todayDoc.id);
      navigateToDocument(todayDoc.id);
    }

    return true; // Date has changed
  }

  return false; // Date has not changed
}

// Document management functions that combine store & routing

// Switch to a document
export async function switchToDocument(documentId) {
  const doc = await loadDocumentSafely(documentId);
  if (doc) {
    navigateToDocument(documentId);
    return true;
  }
  return false;
}

// Create a document (manual creation)
export async function createNewDocument(title) {
  // If no title is provided, create today's document
  if (!title) {
    return await createAndLoadTodayDocument();
  }

  const newDocId = await createDocument(title);
  if (newDocId) {
    await loadDocumentSafely(newDocId);
    navigateToDocument(newDocId);
    return newDocId;
  }
  return null;
}

// Create today's document and load it
export async function createAndLoadTodayDocument() {
  const newDocId = await createTodayDocument();
  if (newDocId) {
    await loadDocumentSafely(newDocId);
    navigateToDocument(newDocId);
    return newDocId;
  }
  return null;
}

// Delete a document and handle navigation
export async function deleteCurrentDocument() {
  const currentDoc = get(currentDocument);
  if (!currentDoc) return false;

  const success = await deleteDocument(currentDoc.id);
  if (success) {
    // Navigate to today's document or create one
    await loadDefaultDocument();
    return true;
  }
  return false;
}

// Helper function to check if HTML content is empty
function isHtmlContentEmpty(htmlContent) {
  if (!htmlContent || htmlContent.trim() === "") {
    return true;
  }

  const textContent = htmlContent.replace(/<[^>]*>/g, "");
  return textContent.trim() === "";
}

// Setup initial blocks for the current document
export async function setupInitialBlocks() {
  logger.info("Setting up initial blocks");

  const allBlocks = get(blocks);

  // Add safety check for empty blocks
  if (!allBlocks || allBlocks.length === 0) {
    logger.warn("No blocks available, skipping initial block setup");
    return { shouldFocus: false };
  }

  logger.info(`Found ${allBlocks.length} blocks, checking first block content`);

  // If there are blocks and the first one isn't empty
  if (!isHtmlContentEmpty(allBlocks[0].content)) {
    logger.info(
      "First block is not empty, creating a new empty block at the top",
    );

    // Create a new block at the top
    const result = await createBlockAtTop(true);

    // Return the result so App.svelte can handle focusing if needed
    return result;
  } else {
    // First block is empty, just mark that a document was loaded
    logger.info("First block is empty, no need to create a new one");
    documentJustLoaded.set(true);
    return { shouldFocus: false };
  }
}
